import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  createZadSupplier,
  fetchZadSupplierById,
  fetchZadSuppliers,
  updateZadSupplier,
  updateZadSupplierStatus,
} from './service'
import {FetchZadSuppliersResponse, ZadSupplierState} from './types'

const initialState: ZadSupplierState = {
  suppliers: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedSupplier: null,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadSupplierState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadSupplierState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

const zadSupplierSlice = createSlice({
  name: 'zadSuppliers',
  initialState,
  reducers: {
    clearZadSupplierError: (state: ZadSupplierState) => {
      clearError(state)
    },
    clearSelectedZadSupplier: (state: ZadSupplierState) => {
      state.selectedSupplier = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadSuppliers.pending, (state: ZadSupplierState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(
      fetchZadSuppliers.rejected,
      (state: ZadSupplierState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.listLoading = false
        state.listInitialized = true
      }
    )
    builder.addCase(
      fetchZadSuppliers.fulfilled,
      (state: ZadSupplierState, action: PayloadAction<FetchZadSuppliersResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.suppliers = action.payload.suppliers
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadSupplierById.pending, (state: ZadSupplierState) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadSupplierById.rejected, (state: ZadSupplierState) => {
      state.actionLoading = false
    })
    builder.addCase(fetchZadSupplierById.fulfilled, (state: ZadSupplierState, action) => {
      state.actionLoading = false
      state.selectedSupplier = action.payload.supplier
    })

    builder.addCase(createZadSupplier.pending, (state: ZadSupplierState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      createZadSupplier.rejected,
      (state: ZadSupplierState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(createZadSupplier.fulfilled, (state: ZadSupplierState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadSupplier.pending, (state: ZadSupplierState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadSupplier.rejected,
      (state: ZadSupplierState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadSupplier.fulfilled, (state: ZadSupplierState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadSupplierStatus.pending, (state: ZadSupplierState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadSupplierStatus.rejected,
      (state: ZadSupplierState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadSupplierStatus.fulfilled, (state: ZadSupplierState) => {
      state.actionLoading = false
      clearError(state)
    })
  },
})

export default zadSupplierSlice.reducer
export const actions = zadSupplierSlice.actions
export {initialState as zadSupplierInitialState}
